import MaskField from '@components/v2/form/MaskField/MaskField';
import Select from '@components/v2/form/Select/Select';
import TextField from '@components/v2/form/TextField/TextField';
import Loading from '@components/v2/ui/Loading/Loading';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Grid, InputAdornment } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { addressTypes } from '../../components/Address/address.interface';
import useAddressZipCode from '../../components/Address/hooks/useAddressZipCode';
import useDefaultAddressType from '../../components/Address/hooks/useDefaultAddressType';
import LangEnum from '../../lang/LangEnum';
import { cepRegex } from '../../utils/regexes';
import { useTranslation } from '../translation/TranslationContext';

interface AddressSectionProps {
  name?: string;
}

const gridItemSize = { xs: 12, sm: 4 };

const AddressSection = ({ name = 'address' }: AddressSectionProps) => {
  const { translate, translateOptions } = useTranslation();
  const { control } = useFormContext();
  const { loadAddress, isLoadingAddress, streetNumberRef } = useAddressZipCode(name);
  useDefaultAddressType(name);

  const city = useWatch({ control, name: `${name}.city` });
  const tooltipHelpTitle = city ? '' : translate(LangEnum.ADDRESS_FORM_TOOLTIP_TEXT);

  const loadingAdornment = isLoadingAddress ? (
    <InputAdornment position="end">
      <Loading size={16} />
    </InputAdornment>
  ) : undefined;

  return (
    <Grid container columnSpacing={1} rowSpacing={1.5}>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.type`}
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              label={translate(LangEnum.ADDRESS_FORM_TYPE_LABEL)}
              options={translateOptions(addressTypes)}
            />
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.zipCode`}
          control={control}
          render={({ field, fieldState }) => (
            <MaskField
              {...field}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              mask={cepRegex}
              label={translate(LangEnum.ADDRESS_FORM_ZIP_CODE_LABEL)}
              onChange={(event) => {
                field.onChange(event);
                loadAddress(event.target.value);
              }}
            />
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.streetDescription`}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              label={translate(LangEnum.ADDRESS_FORM_STREET_DESCRIPTION_LABEL)}
              slotProps={{
                input: { endAdornment: loadingAdornment },
                htmlInput: { maxLength: 200 },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.streetNumber`}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              label={translate(LangEnum.ADDRESS_FORM_NUMBER_LABEL)}
              slotProps={{ htmlInput: { ref: streetNumberRef, maxLength: 200 } }}
            />
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.neighborhood`}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              label={translate(LangEnum.ADDRESS_FORM_NEIGHBORHOOD_LABEL)}
              slotProps={{
                input: { endAdornment: loadingAdornment },
                htmlInput: { maxLength: 200 },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.complement`}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              label={translate(LangEnum.ADDRESS_FORM_COMPLEMENT_LABEL)}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.city`}
          control={control}
          render={({ field, fieldState }) => (
            <Tooltip TooltipContent={tooltipHelpTitle} fullWidth>
              <TextField
                {...field}
                disabled
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                label={translate(LangEnum.ADDRESS_FORM_CITY_LABEL)}
                slotProps={{
                  input: { endAdornment: loadingAdornment },
                  htmlInput: { maxLength: 200 },
                }}
              />
            </Tooltip>
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.state`}
          control={control}
          render={({ field, fieldState }) => (
            <Tooltip TooltipContent={tooltipHelpTitle} fullWidth>
              <TextField
                {...field}
                disabled
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                label={translate(LangEnum.ADDRESS_FORM_STATE_LABEL)}
                slotProps={{
                  input: { endAdornment: loadingAdornment },
                  htmlInput: { maxLength: 200 },
                }}
              />
            </Tooltip>
          )}
        />
      </Grid>
      <Grid size={gridItemSize}>
        <Controller
          name={`${name}.country`}
          control={control}
          render={({ field, fieldState }) => (
            <Tooltip TooltipContent={tooltipHelpTitle} fullWidth>
              <TextField
                {...field}
                disabled
                value={field.value ?? ''}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                label={translate(LangEnum.ADDRESS_FORM_COUNTRY_LABEL)}
                slotProps={{
                  input: { endAdornment: loadingAdornment },
                  htmlInput: { maxLength: 200 },
                }}
              />
            </Tooltip>
          )}
        />
      </Grid>
    </Grid>
  );
};

export default AddressSection;
